import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Copy, FileSpreadsheet, Plus, Trash2, Upload } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { SERVICE_OPTIONS } from "@/lib/services";
import { downloadFile, fmtDuration } from "@/lib/time";
import TemplateDialog from "@/components/templates/TemplateDialog";

const serviceLabel = (s) => SERVICE_OPTIONS.find((o) => o.value === s)?.label || "Other";
const stats = (t) => {
  const subs = t.tasks.reduce((a, x) => a + x.subtasks.length, 0);
  return `${t.tasks.length} tasks · ${subs} subtasks · ${fmtDuration(t.tasks.reduce((a, x) => a + x.duration_minutes, 0))}`;
};

export default function Templates() {
  const [list, setList] = useState([]);
  const [editing, setEditing] = useState(null);
  const fileRef = useRef(null);
  const load = () => api.get("/templates").then((r) => setList(r.data)).catch((e) => toast.error(formatApiError(e)));

  useEffect(() => {
    load();
  }, []);

  const upload = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      const { data } = await api.post("/templates/import", fd);
      toast.success(`Imported "${data.name}". Set the time for each task.`);
      await load();
      setEditing(data);
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (t) => {
    if (!window.confirm(`Delete template "${t.name}"? Existing jobs keep their tasks.`)) return;
    await api.delete(`/templates/${t.id}`).catch((e) => toast.error(formatApiError(e)));
    load();
  };

  const duplicate = ({ id, created_at, ...t }) => setEditing({ ...t, name: `${t.name} (copy)` });

  return (
    <div className="space-y-8 fade-up" data-testid="templates-page">
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-[#0090D0] font-semibold mb-3">Templates</p>
          <h1 className="font-serif-brand font-semibold text-[#103080] text-4xl sm:text-5xl">How we <i className="text-[#0090D0]">do the work</i></h1>
          <p className="mt-3 text-[#0F172A]/60 max-w-xl">Each template is a set of tasks (with a role) broken into subtasks with times, SOP and template links. Pick one when you onboard a client's service.</p>
          <p className="mt-2 text-xs text-[#0F172A]/50 max-w-xl" data-testid="template-import-help">Spreadsheet import: one row per subtask with columns <b>Task</b>, <b>Subtask</b>, <b>Role</b>, <b>Minutes</b> and optional <b>SOP link</b> and <b>Template link</b>. Older files with Section and Task columns still work (sections become tasks).</p>
        </div>
        <div className="flex gap-2 shrink-0">
          <input ref={fileRef} type="file" accept=".xlsx" className="hidden" onChange={upload} data-testid="template-import-input" />
          <button onClick={() => fileRef.current.click()} data-testid="template-import-button"
            className="inline-flex items-center gap-2 rounded-full border border-[#103080]/20 bg-white/70 text-[#103080] font-medium px-5 h-11 text-sm hover:border-[#0090D0] hover:text-[#0090D0] transition-colors duration-200">
            <Upload size={15} /> Import spreadsheet
          </button>
          <button onClick={() => setEditing({})} data-testid="template-new-button"
            className="inline-flex items-center gap-2 rounded-full bg-[#103080] text-white font-medium px-6 h-11 text-sm hover:bg-[#0090D0] transition-colors duration-200">
            <Plus size={16} /> New template
          </button>
        </div>
      </div>
      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="template-list">
        {list.map((t) => (
          <div key={t.id} className="rounded-2xl border border-[#103080]/10 bg-white/80 ambient p-6 flex flex-col" data-testid={`template-card-${t.id}`}>
            <p className="text-xs uppercase tracking-[0.2em] text-[#0090D0] font-semibold">{serviceLabel(t.service)}</p>
            <button onClick={() => setEditing(t)} className="text-left font-serif-brand font-semibold text-xl text-[#103080] mt-2 hover:text-[#0090D0] transition-colors duration-200" data-testid={`template-open-${t.id}`}>{t.name}</button>
            <p className="text-sm text-[#0F172A]/55 mt-2">{stats(t)}</p>
            <ul className="mt-4 space-y-1 text-xs text-[#0F172A]/60 flex-1">
              {t.tasks.slice(0, 5).map((s, i) => <li key={i}>· {s.title} <span className="text-[#0F172A]/40">({s.subtasks.length})</span></li>)}
              {t.tasks.length > 5 && <li>· +{t.tasks.length - 5} more</li>}
            </ul>
            <div className="mt-5 flex gap-2">
              <button onClick={() => duplicate(t)} data-testid={`template-duplicate-${t.id}`} className="inline-flex items-center gap-1 text-xs font-semibold text-[#103080] hover:text-[#0090D0] transition-colors duration-200"><Copy size={12} /> Duplicate</button>
              <button onClick={() => downloadFile(api, `/templates/${t.id}/export`, `${t.name}.xlsx`).catch((e) => toast.error(formatApiError(e)))} data-testid={`template-export-${t.id}`}
                className="inline-flex items-center gap-1 text-xs font-semibold text-[#103080] hover:text-[#0090D0] transition-colors duration-200"><FileSpreadsheet size={12} /> Export</button>
              <button onClick={() => remove(t)} data-testid={`template-delete-${t.id}`} className="ml-auto inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-700 transition-colors duration-200"><Trash2 size={12} /> Delete</button>
            </div>
          </div>
        ))}
      </div>
      <TemplateDialog template={editing} onClose={() => setEditing(null)} onSaved={load} />
    </div>
  );
}
